import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaDownload, FaCheck, FaImages, FaTrash, FaEdit,
  FaExpand, FaPlay, FaTimes, FaArrowLeft, FaArrowRight,
  FaPause, FaEyeSlash, FaEye, FaCheckSquare, FaSquare, FaSearch, FaUser,
  FaShoppingCart, FaCreditCard, FaHeart, FaTag, FaCamera, FaLock, FaLockOpen,
  FaBell, FaBellSlash, FaUpload, FaSpinner,
} from 'react-icons/fa';
import {
  browseEventPhotos, downloadMultiplePhotos, downloadPhoto,
  deletePhoto, getEvent, deleteEvent, fetchPhotoBlob, getFlaskUploadUrl, scanEventForFaces,
  getImageUrl, likePhoto, unlikePhoto, bulkSetPhotoPrice, uploadProfileEmbedding,
  getManagerPhotographers, bulkSetPhotoPhotographer,
  lockEventGallery, unlockEventGallery, watchEventGallery, unwatchEventGallery,
  FACE_PROVIDERS,
} from '../services/api';
import PhotoEditModal from '../components/PhotoEditModal';
import { compressImageForUpload } from '../utils/imageCompression';
import { ownsEvents, isEventManager } from '../utils/roles';

const getPhotoDisplayName = (photo) => {
  const name = photo?.name || photo?.title || photo?.filename || `Photo ${photo?.id || ''}`;
  return name.split('/').pop() || name;
};

// The two interchangeable face-matching engines. Each gets its own accent so a
// user can tell at a glance which one produced the matches on screen.
const FACE_PROVIDER_META = {
  deepface: {
    value: FACE_PROVIDERS.DEEPFACE,
    label: 'DeepFace',
    hint: 'Local · sans frais cloud',
    accent: '#A855F7',
    onAccent: '#ffffff',
  },
  aws_rekognition: {
    value: FACE_PROVIDERS.REKOGNITION,
    label: 'AWS Rekognition',
    hint: 'Cloud AWS · haute précision',
    accent: '#F59E0B',
    onAccent: '#1F2937',
  },
};

const providerMetaFor = (value) => (
  value === FACE_PROVIDERS.REKOGNITION || value === 'aws_rekognition' || value === 'rekognition'
    ? FACE_PROVIDER_META.aws_rekognition
    : FACE_PROVIDER_META.deepface
);

const toNumber = (value) => Number(value) || 0;
const IMAGE_LOAD_CONCURRENCY = 4;
const canDownloadPhoto = (photo, isEventOwner) => (
  isEventOwner
  || Boolean(photo?.has_purchased)
  // Free photos need no purchase, matching what the API allows.
  || Number(photo?.price || 0) <= 0
);

const EventBrowse = () => {
  const { user } = useAuth();
  const { items: cartItems, toggleItem: toggleCartItem, removeItem: removeCartItem } = useCart();
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [updatingPrice, setUpdatingPrice] = useState(false);
  const [photoBlobs, setPhotoBlobs] = useState({});
  const [likingPhotoIds, setLikingPhotoIds] = useState([]);

  // Face scan state (Flask matching service — DeepFace or AWS Rekognition)
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [showOnlyMatches, setShowOnlyMatches] = useState(false);
  // Set by the API for a private event's guests: no gallery before a scan.
  const [requiresFaceScan, setRequiresFaceScan] = useState(false);
  // Set by the API while the photographer keeps the gallery closed. Distinct
  // from `galleryLocked` below, which is the private-event face-scan gate.
  const [galleryClosed, setGalleryClosed] = useState(false);
  const [isWatching, setIsWatching] = useState(false);
  const [watchersCount, setWatchersCount] = useState(0);
  const [lockBusy, setLockBusy] = useState(false);
  const [faceProvider, setFaceProvider] = useState(FACE_PROVIDERS.REKOGNITION);
  const deepFaceModel = 'SFace';
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [capturedFacePreview, setCapturedFacePreview] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const isRekognition = faceProvider === FACE_PROVIDERS.REKOGNITION;
  const providerMeta = providerMetaFor(faceProvider);
  // Colour the results after the engine that actually produced them, so
  // switching the dropdown afterwards does not recolour an existing scan.
  const resultMeta = providerMetaFor(scanResult?.provider);
  const scanAccent = scanResult ? resultMeta.accent : providerMeta.accent;
  const scanOnAccent = scanResult ? resultMeta.onAccent : providerMeta.onAccent;

  // Lightbox / slideshow state
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [slideshowActive, setSlideshowActive] = useState(false);

  // Photographers and event managers both run events rather than buy from
  // them, so neither gets the face-scan or purchase flow.
  const isEventStaff = ownsEvents(user);
  const isEventOwner = isEventStaff
    && event?.photographer_id != null
    && Number(event.photographer_id) === Number(user?.id);
  const cartPhotoIds = cartItems.map(item => item.id);

  // Re-crediting photos is a manager's job, so the roster is only ever fetched
  // for the manager who owns this event.
  const canRecredit = isEventOwner && isEventManager(user);
  const [roster, setRoster] = useState([]);
  const [updatingCredit, setUpdatingCredit] = useState(false);

  useEffect(() => {
    fetchAll();
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canRecredit) {
      setRoster([]);
      return;
    }

    getManagerPhotographers()
      .then(response => setRoster(response.photographers || []))
      .catch(() => setRoster([]));
  }, [canRecredit]);

  useEffect(() => {
    return () => stopFaceCamera();
  }, []);

  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraActive]);

  // Slideshow auto-advance
  useEffect(() => {
    if (!slideshowActive || lightboxIndex === null || photos.length === 0) return;
    const timer = setInterval(() => {
      setLightboxIndex(prev => (prev + 1) % photos.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slideshowActive, lightboxIndex, photos.length]);

  // Keyboard navigation for lightbox
  const handleKeyDown = useCallback((e) => {
    if (lightboxIndex === null) return;
    if (e.key === 'ArrowRight') setLightboxIndex(prev => (prev + 1) % photos.length);
    if (e.key === 'ArrowLeft') setLightboxIndex(prev => (prev - 1 + photos.length) % photos.length);
    if (e.key === 'Escape') closeLightbox();
  }, [lightboxIndex, photos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const fetchImageBlob = async (photo) => {
    try {
      const blobUrl = await fetchPhotoBlob(photo.id);
      setPhotoBlobs(prev => ({ ...prev, [photo.id]: blobUrl }));
    } catch (err) {
      if (photo.filename) {
        setPhotoBlobs(prev => ({ ...prev, [photo.id]: getFlaskUploadUrl(photo.filename) }));
      }
      console.error('Error loading image blob:', err);
    }
  };

  const fetchImageBlobs = async (items) => {
    let nextIndex = 0;
    const workerCount = Math.min(IMAGE_LOAD_CONCURRENCY, items.length);
    const workers = Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length) {
        const photo = items[nextIndex];
        nextIndex += 1;
        await fetchImageBlob(photo);
      }
    });

    await Promise.all(workers);
  };

  const fetchAll = async () => {
    try {
      const [eventRes, photosRes] = await Promise.all([
        getEvent(eventId).catch(() => null),
        browseEventPhotos(eventId),
      ]);
      if (eventRes) {
        setEvent(eventRes.event || eventRes.data?.event || eventRes);
      }
      // On a private event the API returns only the photos this guest has
      // already been matched to, and asks us to keep the gallery shut until
      // they have identified themselves.
      setRequiresFaceScan(Boolean(photosRes.requires_face_scan));
      setGalleryClosed(Boolean(photosRes.gallery_locked));
      setIsWatching(Boolean(photosRes.is_watching));
      setWatchersCount(toNumber(photosRes.watchers_count));
      const fetchedPhotos = photosRes.photos || [];
      setPhotos(fetchedPhotos);
      fetchImageBlobs(fetchedPhotos);
    } catch (err) {
      setError(err.message || 'Impossible de charger les photos');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSaved = (updatedPhoto) => {
    setPhotos(prev => prev.map(p =>
      p.id === updatedPhoto.id ? { ...p, ...updatedPhoto } : p
    ));
    setMessage({ type: 'success', text: 'Photo updated successfully!' });
  };

  const handleDeletePhoto = async (photoId) => {
    if (deletingSelected) return;
    if (!window.confirm('Supprimer cette photo ? Cette action est irréversible.')) return;
    setDeletingPhoto(photoId);
    setMessage({ type: '', text: '' });
    try {
      await deletePhoto(photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setSelectedPhotos(prev => prev.filter(id => id !== photoId));
      removeCartItem(photoId);
      setMessage({ type: 'success', text: 'Photo deleted.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Impossible de supprimer la photo' });
    } finally {
      setDeletingPhoto(null);
    }
  };

  const handleDeleteSelected = async () => {
    if (!isEventOwner || deletingSelected || deletingPhoto !== null || selectedPhotos.length === 0) return;
    const photoIds = [...selectedPhotos];
    if (!window.confirm(`Supprimer les ${photoIds.length} photos sélectionnées ? Cette action est irréversible.`)) return;

    setDeletingSelected(true);
    setMessage({ type: '', text: '' });
    closeLightbox();
    const deletedIds = [];
    const failures = [];
    try {
      for (const photoId of photoIds) {
        try {
          await deletePhoto(photoId);
          deletedIds.push(photoId);
          removeCartItem(photoId);
        } catch (err) {
          failures.push(err.message || 'Impossible de supprimer la photo');
        }
      }
      setPhotos(prev => prev.filter(photo => !deletedIds.includes(photo.id)));
      setSelectedPhotos(prev => prev.filter(id => !deletedIds.includes(id)));
      setMessage({
        type: failures.length ? 'error' : 'success',
        text: failures.length
          ? `${deletedIds.length} photo(s) supprimée(s). ${failures.length} suppression(s) échouée(s) ; les photos concernées restent sélectionnées. ${failures[0]}`
          : `${deletedIds.length} photo(s) supprimée(s).`,
      });
    } finally {
      setDeletingSelected(false);
    }
  };

  const handleToggleLike = async (photo) => {
    if (likingPhotoIds.includes(photo.id)) return;

    const wasLiked = Boolean(photo.is_liked);
    const optimisticLikes = Math.max(0, toNumber(photo.likes_count) + (wasLiked ? -1 : 1));
    setLikingPhotoIds(prev => [...prev, photo.id]);
    setPhotos(prev => prev.map(p => (
      p.id === photo.id
        ? {
            ...p,
            is_liked: !wasLiked,
            likes_count: optimisticLikes,
          }
        : p
    )));

    try {
      const response = wasLiked
        ? await unlikePhoto(photo.id)
        : await likePhoto(photo.id);

      setPhotos(prev => prev.map(p => (
        p.id === photo.id
          ? {
              ...p,
              is_liked: Boolean(response.liked),
              likes_count: toNumber(response.likes_count),
            }
          : p
      )));
    } catch (err) {
      setPhotos(prev => prev.map(p => (
        p.id === photo.id
          ? {
              ...p,
              is_liked: wasLiked,
              likes_count: toNumber(photo.likes_count),
            }
          : p
      )));
      setMessage({ type: 'error', text: err.message || "Impossible de mettre à jour la mention J'aime" });
    } finally {
      setLikingPhotoIds(prev => prev.filter(id => id !== photo.id));
    }
  };

  const handleToggleWatch = async () => {
    setLockBusy(true);
    setMessage({ type: '', text: '' });
    try {
      const res = isWatching
        ? await unwatchEventGallery(eventId)
        : await watchEventGallery(eventId);
      // The photographer may have opened the gallery since this page loaded.
      if (res.is_locked === false) {
        await fetchAll();
      }
      setIsWatching(Boolean(res.is_watching));
      setMessage({
        type: 'success',
        text: res.message || (res.is_watching ? "Vous serez prévenu dès l'ouverture." : 'Vous ne serez plus prévenu.'),
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || "Impossible de mettre à jour l'alerte." });
    } finally {
      setLockBusy(false);
    }
  };

  const handleToggleGalleryLock = async () => {
    const locking = !event?.is_locked;
    if (!locking && !window.confirm(
      watchersCount > 0
        ? `Ouvrir la galerie ? ${watchersCount} personne(s) en attente seront prévenues.`
        : 'Ouvrir la galerie à tous les invités ?'
    )) return;

    setLockBusy(true);
    setMessage({ type: '', text: '' });
    try {
      const res = locking
        ? await lockEventGallery(eventId)
        : await unlockEventGallery(eventId);
      setEvent(prev => ({ ...prev, is_locked: locking }));
      setWatchersCount(locking ? toNumber(res.watchers_count) : 0);
      setMessage({ type: 'success', text: res.message || (locking ? 'Galerie verrouillée.' : 'Galerie ouverte.') });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Impossible de modifier le verrouillage.' });
    } finally {
      setLockBusy(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Supprimer tout cet événement et toutes ses photos ? Cette action est irréversible.')) return;
    try {
      await deleteEvent(eventId);
      navigate('/photographer', {
        state: { successMessage: "Événement supprimé avec succès." }
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || "Impossible de supprimer l'événement" });
    }
  };

  const togglePhotoSelection = (photoId) => {
    if (deletingSelected) return;
    setSelectedPhotos(prev =>
      prev.includes(photoId) ? prev.filter(id => id !== photoId) : [...prev, photoId]
    );
  };

  const selectAll = () => {
    if (deletingSelected) return;
    setSelectedPhotos(selectedPhotos.length === photos.length ? [] : photos.map(p => p.id));
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadSingle = async (photo) => {
    if (!canDownloadPhoto(photo, isEventStaff)) {
      setMessage({ type: 'error', text: 'Achetez cette photo avant de la télécharger.' });
      return;
    }

    try {
      const blob = await downloadPhoto(photo.id);
      triggerBlobDownload(blob, getPhotoDisplayName(photo) || `photo_${photo.id}`);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Le téléchargement a échoué' });
    }
  };

  const toggleCartPhoto = (photo) => {
    toggleCartItem(photo, eventId);
  };

  const markPurchased = (photoIds) => {
    setPhotos(prev => prev.map(photo =>
      photoIds.includes(photo.id) ? { ...photo, has_purchased: true } : photo
    ));
  };

  useEffect(() => {
    const handleCartCheckoutSuccess = (event) => {
      const photoIds = event.detail?.photoIds || [];
      if (photoIds.length === 0) return;

      markPurchased(photoIds);
      setMessage({
        type: 'info',
        text: `Paiement lance avec ${event.detail?.methodLabel || 'CinetPay'}. Les photos seront disponibles apres confirmation.`,
      });
    };

    window.addEventListener('photomatch:cart-checkout-success', handleCartCheckoutSuccess);
    return () => window.removeEventListener('photomatch:cart-checkout-success', handleCartCheckoutSuccess);
  }, []);

  const handleDownloadSelected = async () => {
    if (selectedPhotos.length === 0) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner au moins une photo' });
      return;
    }

    const downloadablePhotoIds = photos
      .filter(photo => selectedPhotos.includes(photo.id) && canDownloadPhoto(photo, isEventOwner))
      .map(photo => photo.id);

    if (downloadablePhotoIds.length === 0) {
      setMessage({ type: 'error', text: 'Sélectionnez des photos gratuites ou achetées à télécharger.' });
      return;
    }

    setDownloading(true);
    setMessage({ type: '', text: '' });
    try {

      console.log("Requesting download for photo IDs:", downloadablePhotoIds);
      
      const blob = await downloadMultiplePhotos({ photo_ids: downloadablePhotoIds });

      console.log("Blob received for multiple photos:", blob);
      triggerBlobDownload(blob, `event_${eventId}_photos.zip`);
      setMessage({ type: 'success', text: `${downloadablePhotoIds.length} photos téléchargées avec succès !` });
      setSelectedPhotos([]);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Le téléchargement a échoué' });
    } finally {
      setDownloading(false);
    }
  };

  const handleBulkSetPrice = async () => {
    if (!isEventOwner) {
      setMessage({ type: 'error', text: 'Only the event owner can update photo prices.' });
      return;
    }

    if (selectedPhotos.length === 0) {
      setMessage({ type: 'error', text: 'Sélectionnez au moins une photo pour mettre à jour le prix.' });
      return;
    }

    const priceInput = window.prompt('Définir le prix des photos sélectionnées', '0');
    if (priceInput === null) return;

    const normalizedPrice = Number(priceInput.replace(',', '.'));
    if (!Number.isFinite(normalizedPrice) || normalizedPrice < 0) {
      setMessage({ type: 'error', text: 'Saisissez un prix valide de 0 ou plus.' });
      return;
    }

    setUpdatingPrice(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await bulkSetPhotoPrice(selectedPhotos, normalizedPrice);
      const updatedIds = response.photo_ids || selectedPhotos;
      const nextPrice = Number(response.price ?? normalizedPrice);
      const isPurchasable = Boolean(response.is_purchasable ?? nextPrice > 0);

      setPhotos(prev => prev.map(photo => (
        updatedIds.includes(photo.id)
          ? { ...photo, price: nextPrice, is_purchasable: isPurchasable }
          : photo
      )));
      setMessage({ type: 'success', text: `Prix mis à jour pour ${updatedIds.length} photo${updatedIds.length !== 1 ? 's' : ''}.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Impossible de mettre à jour les prix' });
    } finally {
      setUpdatingPrice(false);
    }
  };

  /**
   * Re-credit the selected photos to a different photographer.
   *
   * Credit is a byline: it changes who is shown as having taken the photo, and
   * never who the sale belongs to.
   */
  const handleBulkSetPhotographer = async (photographerId) => {
    if (!canRecredit || selectedPhotos.length === 0 || !photographerId) {
      return;
    }

    setUpdatingCredit(true);
    setMessage({ type: '', text: '' });
    try {
      await bulkSetPhotoPhotographer(selectedPhotos, Number(photographerId));

      const credited = roster.find(p => Number(p.id) === Number(photographerId));
      setPhotos(prev => prev.map(photo => (
        selectedPhotos.includes(photo.id)
          ? { ...photo, photographer_id: Number(photographerId), photographer: credited || photo.photographer }
          : photo
      )));
      setMessage({
        type: 'success',
        text: `${selectedPhotos.length} photo${selectedPhotos.length !== 1 ? 's' : ''} créditée${selectedPhotos.length !== 1 ? 's' : ''} à ${credited?.name || 'ce photographe'}.`,
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Impossible de modifier le crédit photo' });
    } finally {
      setUpdatingCredit(false);
    }
  };

  const startFaceCamera = async () => {
    setMessage({ type: '', text: '' });
    setCameraLoading(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 960 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: "Impossible d'accéder à votre webcam. Autorisez l'accès à la caméra puis réessayez.",
      });
    } finally {
      setCameraLoading(false);
    }
  };

  const stopFaceCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraActive(false);
  };

  const captureFacePhoto = () => new Promise((resolve, reject) => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      reject(new Error("La caméra n'est pas encore prête."));
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 960;
    canvas.height = video.videoHeight || 720;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Impossible de capturer une image depuis la webcam."));
        return;
      }

      resolve(new File([blob], `webcam-face-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  });

  const runFaceScan = async ({ photoFile = null } = {}) => {
    if (scanning) return;
    setScanning(true);
    setScanResult(null);
    setMessage({ type: '', text: '' });
    try {
      let referenceFile = null;

      if (photoFile) {
        referenceFile = await compressImageForUpload(photoFile, undefined, undefined, 'Photo webcam');
        setCapturedFacePreview(URL.createObjectURL(referenceFile));

        // DeepFace matches against a stored embedding, so the capture has to be
        // encoded and saved first. Rekognition searches with the image itself,
        // which travels with the scan request below.
        if (!isRekognition) {
          const formData = new FormData();
          formData.append('photo', referenceFile);
          await uploadProfileEmbedding(formData, deepFaceModel);
        }
      }

      const result = await scanEventForFaces(eventId, faceProvider, {
        model: deepFaceModel,
        photoFile: referenceFile,
      });

      setScanResult(result);
      setShowOnlyMatches(true);
      setMessage({ type: 'success', text: result.message || 'Recherche terminée.' });

      // A private event withholds its photos until a scan matches them, so the
      // ones just unlocked have to be pulled back from the API — the scan
      // response carries the match list, not the gallery entries.
      if (requiresFaceScan) {
        await fetchAll();
      }
    } catch (err) {
      // Locked since this page loaded: show the waiting list, not an error.
      if (err.gallery_locked) {
        setGalleryClosed(true);
        setMessage({ type: 'error', text: err.message });
        return;
      }
      const detail = err.error && err.error !== err.message ? `: ${err.error}` : '';
      setMessage({
        type: 'error',
        text: `${err.message || "Le scan du visage a échoué. Assurez-vous que votre visage est bien visible."}${detail}`
      });
    } finally {
      setScanning(false);
    }
  };

  const handleScanMyFace = () => runFaceScan();

  const handleScanWithWebcam = async () => {
    try {
      const photoFile = await captureFacePhoto();
      stopFaceCamera();
      await runFaceScan({ photoFile });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || "Impossible de scanner votre visage avec la webcam." });
    }
  };

  // Keep the gallery shut on a private event until this guest has scanned.
  // Once a scan has run, its outcome speaks for itself — including "no match",
  // which must show the empty-result message rather than the lock screen again.
  const galleryLocked = requiresFaceScan && !scanResult;

  const matchedPhotoIds = new Set((scanResult?.matched_photos || []).map(p => p.id));
  const displayedPhotos = showOnlyMatches && scanResult
    ? photos.filter(p => matchedPhotoIds.has(p.id))
    : photos;

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setSlideshowActive(false);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    setSlideshowActive(false);
  };

  const lightboxPrev = () => setLightboxIndex(prev => (prev - 1 + photos.length) % photos.length);
  const lightboxNext = () => setLightboxIndex(prev => (prev + 1) % photos.length);

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement des photos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const currentPhoto = lightboxIndex !== null ? photos[lightboxIndex] : null;
  const cartPhotos = cartItems;
  const cartTotal = cartPhotos.reduce((sum, photo) => sum + Number(photo.price || 0), 0);
  const coverPhotoUrl = event?.cover_photo ? getImageUrl(event.cover_photo) : null;
  const selectedDownloadableCount = photos.filter(photo =>
    selectedPhotos.includes(photo.id) && canDownloadPhoto(photo, isEventOwner)
  ).length;

  return (
    <>
      <div className="container">
        <div style={{
          width: '100%',
          height: '260px',
          borderRadius: '14px',
          overflow: 'hidden',
          marginBottom: '1.5rem',
          background: 'linear-gradient(135deg, #E8EDFF 0%, var(--primary) 100%)',
          boxShadow: '0 4px 14px var(--shadow)',
          position: 'relative',
        }}>
          {coverPhotoUrl ? (
            <img
              src={coverPhotoUrl}
              alt={`${event?.name || 'Event'} cover`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.9)',
              fontSize: '4rem',
            }}>
              <FaImages />
            </div>
          )}
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '2rem',
            color: 'white',
            background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)',
          }}>
            <h2 style={{ margin: 0, fontSize: '1.8rem' }}>{event?.name || "Photos de l'événement"}</h2>
          </div>
        </div>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem'
        }}>
          <div>
            <h1 style={{ color: 'var(--primary-orange)', marginBottom: '0.25rem' }}>
              {event?.name || "Photos de l'événement"}
            </h1>
            {event?.location && (
              <p style={{ color: 'var(--gray)', margin: '0 0 0.25rem' }}>📍 {event.location}</p>
            )}
            {event?.event_date && (
              <p style={{ color: 'var(--gray)', margin: '0 0 0.25rem' }}>
                📅 {new Date(event.event_date).toLocaleDateString()}
              </p>
            )}
            <p style={{ color: 'var(--gray)', margin: 0 }}>
              {photos.length} photos • {selectedPhotos.length} selected
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {!isEventStaff && !galleryClosed && event?.selfie_search_enabled && (
              <>
                <button
                  onClick={handleScanMyFace}
                  disabled={scanning}
                  title={`Recherche avec ${providerMeta.label}`}
                  style={{
                    padding: '0.7rem 1.2rem', background: scanning ? 'var(--gray)' : providerMeta.accent,
                    color: scanning ? 'white' : providerMeta.onAccent, border: 'none', borderRadius: '8px',
                    cursor: scanning ? 'not-allowed' : 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                  <FaSearch /> {scanning ? 'Scan...' : 'Trouver mes photos'}
                </button>

                <button
                  onClick={cameraActive ? stopFaceCamera : startFaceCamera}
                  disabled={scanning || cameraLoading}
                  style={{
                    padding: '0.7rem 1.2rem',
                    background: cameraActive ? '#EF4444' : 'var(--surface-2)',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: scanning || cameraLoading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                  {cameraActive ? <FaTimes /> : <FaCamera />}
                  {cameraLoading ? 'Ouverture...' : cameraActive ? 'Fermer webcam' : 'Scanner webcam'}
                </button>

                {/* Which engine performs the search */}
                <select
                  value={faceProvider}
                  onChange={e => setFaceProvider(e.target.value)}
                  disabled={scanning}
                  title={providerMeta.hint}
                  aria-label="Moteur de reconnaissance faciale"
                  style={{
                    padding: '0.7rem 0.6rem', background: '#3B82F6', color: providerMeta.accent,
                    border: `2px solid ${providerMeta.accent}`, borderRadius: '8px',
                    fontWeight: '700', fontSize: '0.85rem',
                    cursor: scanning ? 'not-allowed' : 'pointer',
                  }}
                >
                  <option value={FACE_PROVIDERS.DEEPFACE}>DeepFace (local)</option>
                  <option value={FACE_PROVIDERS.REKOGNITION}>AWS Rekognition</option>
                </select>

                {!isRekognition && <span style={{ color: 'var(--gray)' }}>SFace</span>}

                {scanResult && (
                  <button
                    onClick={() => setShowOnlyMatches(s => !s)}
                    style={{
                      padding: '0.7rem 1.2rem',
                      background: showOnlyMatches ? scanAccent : 'transparent',
                      color: showOnlyMatches ? scanOnAccent : scanAccent,
                      border: `2px solid ${scanAccent}`, borderRadius: '8px',
                      cursor: 'pointer', fontWeight: '600',
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                    }}
                  >
                    <FaUser /> {showOnlyMatches ? 'Tout afficher' : `Mes correspondances (${scanResult.matched_photos.length})`}
                  </button>
                )}
              </>
            )}
            {isEventOwner && (
              <>
                <button
                  type="button"
                  onClick={() => navigate(`/event/${eventId}/photos`)}
                  style={{
                    padding: '0.7rem 1.2rem', background: 'var(--primary-green)',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                  <FaUpload /> Importer des photos supplémentaires
                </button>
                <button
                  onClick={handleToggleGalleryLock}
                  disabled={lockBusy}
                  title={event?.is_locked
                    ? 'Les invités ne voient rien tant que la galerie est verrouillée'
                    : 'Fermer la galerie le temps de finir la mise en ligne'}
                  style={{
                    padding: '0.7rem 1.2rem',
                    background: lockBusy ? 'var(--gray)' : event?.is_locked ? 'var(--primary-green)' : 'var(--dark-gray)',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: lockBusy ? 'not-allowed' : 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                  {event?.is_locked ? <FaLockOpen /> : <FaLock />}
                  {event?.is_locked
                    ? `Ouvrir la galerie${watchersCount > 0 ? ` (${watchersCount} en attente)` : ''}`
                    : 'Verrouiller la galerie'}
                </button>
                <button
                  onClick={() => navigate(`/event/${eventId}/edit`)}
                  style={{
                    padding: '0.7rem 1.2rem', background: 'var(--primary-orange)',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                <FaEdit /> Modifier l'événement
                </button>
                <button
                  onClick={handleDeleteEvent}
                  style={{
                    padding: '0.7rem 1.2rem', background: '#EF4444',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}
                >
                  <FaTrash /> Supprimer l'événement
                </button>
              </>
            )}
            {photos.length > 0 && (
              <button
                onClick={() => { openLightbox(0); setSlideshowActive(true); }}
                style={{
                  padding: '0.7rem 1.2rem', background: 'var(--primary-green)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <FaPlay /> Slideshow
              </button>
            )}
            <button
              onClick={selectAll}
              style={{
                padding: '0.7rem 1.2rem', background: 'var(--primary-green)',
                color: 'white', border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontWeight: '600',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
              }}
            >
              <FaCheck /> {selectedPhotos.length === photos.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
            {selectedDownloadableCount > 0 && (
              <button
                onClick={handleDownloadSelected}
                disabled={downloading}
                style={{
                  padding: '0.7rem 1.2rem',
                  background: 'var(--primary-orange)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: downloading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <FaDownload /> {downloading ? 'Téléchargement...' : `Télécharger (${selectedDownloadableCount})`}
              </button>
            )}
            {isEventOwner && selectedPhotos.length > 0 && (
              <button
                onClick={handleBulkSetPrice}
                disabled={updatingPrice}
                style={{
                  padding: '0.7rem 1.2rem',
                  background: updatingPrice ? 'var(--gray)' : 'var(--primary-orange)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: updatingPrice ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <FaTag /> {updatingPrice ? 'Enregistrement...' : `Définir le prix (${selectedPhotos.length})`}
              </button>
            )}
            {isEventOwner && selectedPhotos.length > 0 && (
              <button
                type="button"
                onClick={handleDeleteSelected}
                disabled={deletingSelected || deletingPhoto !== null}
                style={{
                  padding: '0.7rem 1.2rem', background: '#EF4444',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: deletingSelected || deletingPhoto !== null ? 'not-allowed' : 'pointer',
                  fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                <FaTrash /> {deletingSelected ? 'Suppression...' : `Supprimer la sélection (${selectedPhotos.length})`}
              </button>
            )}
            {canRecredit && selectedPhotos.length > 0 && (
              <select
                value=""
                disabled={updatingCredit || roster.length === 0}
                onChange={(e) => handleBulkSetPhotographer(e.target.value)}
                title={roster.length === 0
                  ? "Ajoutez d'abord des photographes à votre équipe"
                  : 'Attribuer les photos sélectionnées à un photographe'}
                style={{
                  padding: '0.7rem 1.2rem',
                  background: updatingCredit ? 'var(--gray)' : '#A855F7',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: updatingCredit || roster.length === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                }}
              >
                <option value="">
                  {updatingCredit
                    ? 'Enregistrement...'
                    : `📷 Attribuer à... (${selectedPhotos.length})`}
                </option>
                {roster.map(photographer => (
                  <option key={photographer.id} value={photographer.id}>
                    {photographer.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {photos.length > 0 && (
          <div style={{
            marginBottom: '2rem', padding: '1.5rem',
            background: 'var(--bg)', borderRadius: '12px', textAlign: 'center'
          }}>
            <h3 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>Statistiques photo</h3>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem'
            }}>
              {[
                { value: photos.length, label: 'Photos au total' },
                { value: photos.reduce((s, p) => s + toNumber(p.matches_count), 0), label: 'Correspondances au total' },
                { value: photos.reduce((s, p) => s + toNumber(p.downloads_count), 0), label: 'Téléchargements au total' },
                { value: `${photos.reduce((s, p) => s + toNumber(p.file_size_mb), 0).toFixed(2)} MB`, label: 'Taille totale' },
              ].map(({ value, label }) => (
                <div key={label}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-orange)' }}>{value}</div>
                  <div style={{ color: 'var(--gray)' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {scanning && (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{
              display: 'flex', alignItems: 'center', gap: '1rem',
              padding: '1.25rem', marginBottom: '1.5rem',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: '12px', boxShadow: '0 3px 10px var(--shadow)',
              position: 'sticky', top: '1rem', zIndex: 10,
            }}
          >
            <span aria-hidden="true" style={{ display: 'flex', flexShrink: 0 }}>
              <FaSpinner style={{ color: providerMeta.accent, fontSize: '30px', animation: 'cameraLensSpin 1s linear infinite' }} />
            </span>
            <div>
              <strong>Recherche de vos photos en cours...</strong>
              <p style={{ margin: '0.35rem 0 0', color: 'var(--gray)' }}>
                Nous recherchons les photos correspondant à votre selfie. Cela peut prendre quelques instants. Gardez cette page ouverte.
              </p>
            </div>
          </div>
        )}

        {message.text && (
          <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
            {message.text}
          </div>
        )}

        {!isEventStaff && cameraActive && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            boxShadow: '0 3px 10px var(--shadow)',
          }}>
            <div style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              <div style={{
                borderRadius: '10px',
                overflow: 'hidden',
                background: '#3B82F6',
                minHeight: '220px',
                flex: '1 1 280px',
              }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    maxHeight: '360px',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </div>

              <button
                onClick={handleScanWithWebcam}
                disabled={scanning}
                style={{
                  padding: '0.8rem 1.2rem',
                  background: scanning ? 'var(--gray)' : providerMeta.accent,
                  color: scanning ? 'white' : providerMeta.onAccent,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: scanning ? 'not-allowed' : 'pointer',
                  fontWeight: '700',
                  minHeight: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.45rem',
                  whiteSpace: 'nowrap',
                  flex: '1 1 190px',
                }}
              >
                <FaSearch /> {scanning ? 'Recherche...' : 'Capturer et chercher'}
              </button>
            </div>
          </div>
        )}

        {!isEventStaff && capturedFacePreview && !cameraActive && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.85rem 1rem',
            background: `${scanAccent}14`,
            border: `1px solid ${scanAccent}38`,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}>
            <img
              src={capturedFacePreview}
              alt="Visage scanné"
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: `2px solid ${scanAccent}`,
              }}
            />
            <div>
              <strong style={{ color: scanAccent }}>Visage webcam utilisé pour cette recherche</strong>
              <p style={{ margin: '0.15rem 0 0', color: 'var(--gray)', fontSize: '0.9rem' }}>
                Vous pouvez relancer un scan webcam si vous souhaitez utiliser une nouvelle capture.
              </p>
            </div>
          </div>
        )}

        {!isEventStaff && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem 1.25rem',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            boxShadow: '0 3px 10px var(--shadow)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', color: 'var(--dark-gray)' }}>
              <FaShoppingCart style={{ color: 'var(--primary-orange)', fontSize: '1.25rem' }} />
              <strong>{cartPhotoIds.length} photo{cartPhotoIds.length !== 1 ? 's' : ''} in cart</strong>
              <span style={{ color: 'var(--gray)' }}>{cartTotal.toFixed(2)} F</span>
            </div>
            <button
              onClick={() => navigate('/checkout')}
              disabled={cartPhotoIds.length === 0}
              style={{
                padding: '0.7rem 1.2rem',
                background: cartPhotoIds.length === 0 ? 'var(--gray)' : 'var(--primary-orange)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: cartPhotoIds.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
              }}
            >
              <FaCreditCard /> Payer maintenant
            </button>
          </div>
        )}

        {/* Scan results summary banner */}
        {scanResult && !scanning && (
          <div style={{
            padding: '1rem 1.5rem', marginBottom: '1.5rem',
            background: `linear-gradient(135deg, ${scanAccent}14, ${scanAccent}05)`,
            border: `1px solid ${scanAccent}4d`, borderRadius: '12px',
            display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap',
          }}>
            <FaSearch style={{ color: scanAccent, fontSize: '1.4rem', flexShrink: 0 }} />
            <div>
              <strong style={{ color: scanAccent }}>
                Scan {resultMeta.label}
                {scanResult.model && scanResult.model !== resultMeta.label ? ` - ${scanResult.model}` : ''}
                {' '}terminé
              </strong>
              <span style={{ color: 'var(--dark-gray)', marginLeft: '0.5rem' }}>
                {scanResult.matched_photos.length} match{scanResult.matched_photos.length !== 1 ? 'es' : ''} found
                {' '}out of {scanResult.total_scanned} photos scanned
                {scanResult.new_matches > 0 && (
                  <span style={{ color: 'var(--primary-green)', marginLeft: '0.5rem' }}>
                    (+{scanResult.new_matches} new)
                  </span>
                )}
                {scanResult.skipped > 0 && (
                  <span style={{ color: 'var(--gray)', marginLeft: '0.5rem' }}>
                    · {scanResult.skipped} skipped
                  </span>
                )}
              </span>
              {scanResult.provider === 'aws_rekognition' && (
                <div style={{ color: 'var(--gray)', fontSize: '0.8rem', marginTop: '0.35rem' }}>
                  Région {scanResult.region} · seuil de similarité {scanResult.threshold}%
                  {scanResult.index?.newly_indexed_faces > 0 && (
                    <> · {scanResult.index.newly_indexed_faces} visage(s) indexé(s) pour cet événement</>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {galleryClosed ? (
          <div style={{
            padding: '4rem 2rem', textAlign: 'center',
            background: 'var(--surface)', borderRadius: '15px',
            boxShadow: '0 4px 8px var(--shadow)'
          }}>
            <FaLock style={{ fontSize: '3.5rem', color: 'var(--primary-orange)', marginBottom: '1.25rem' }} />
            <h3 style={{ color: 'var(--dark-gray)', marginBottom: '0.75rem' }}>
              Galerie en préparation
            </h3>
            <p style={{ color: 'var(--gray)', maxWidth: '520px', margin: '0 auto 1.75rem' }}>
              Le photographe est encore en train de mettre les photos en ligne.
              {isWatching
                ? " Vous êtes sur la liste : nous vous préviendrons dès l'ouverture de la galerie."
                : " Ajoutez-vous à la liste d'attente pour être prévenu dès qu'elle sera ouverte."}
            </p>
            <button
              className="btn btn-primary"
              onClick={handleToggleWatch}
              disabled={lockBusy}
              style={isWatching ? {
                background: 'transparent',
                color: 'var(--primary-orange)',
                border: '2px solid var(--primary-orange)',
              } : undefined}
            >
              {isWatching ? <FaBellSlash /> : <FaBell />}
              {' '}
              {lockBusy ? 'Un instant...' : isWatching ? 'Ne plus me prévenir' : 'Me prévenir'}
            </button>
          </div>
        ) : galleryLocked && !event?.selfie_search_enabled ? (
          <div className="empty-state"><p>La recherche par selfie est désactivée pour cet événement. Contactez le photographe pour accéder à vos photos.</p></div>
        ) : galleryLocked ? (
          <div style={{
            padding: '4rem 2rem', textAlign: 'center',
            background: 'var(--surface)', borderRadius: '15px',
            boxShadow: '0 4px 8px var(--shadow)'
          }}>
            <FaLock style={{ fontSize: '3.5rem', color: providerMeta.accent, marginBottom: '1.25rem' }} />
            <h3 style={{ color: 'var(--dark-gray)', marginBottom: '0.75rem' }}>
              Cet événement est privé
            </h3>
            <p style={{ color: 'var(--gray)', maxWidth: '520px', margin: '0 auto 1.75rem' }}>
              Pour protéger la confidentialité des invités, les photos ne sont pas affichées
              tant que vous ne vous êtes pas identifié. Lancez une reconnaissance faciale :
              vous ne verrez que les photos sur lesquelles vous apparaissez.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary"
                onClick={handleScanMyFace}
                disabled={scanning}
                style={{
                  background: providerMeta.accent,
                  borderColor: providerMeta.accent,
                  color: providerMeta.onAccent,
                }}
              >
                <FaSearch /> {scanning ? 'Analyse en cours...' : 'Trouver mes photos'}
              </button>
              {/* .btn-secondary is white-on-transparent for the landing hero, so it
                  disappeared against this white card. Styled here like the toolbar's
                  webcam button, keeping the lock-screen button geometry. */}
              <button
                onClick={startFaceCamera}
                disabled={scanning || cameraLoading}
                style={{
                  padding: '1rem 2rem',
                  background: '#3B82F6',
                  color: '#ffffff',
                  border: '2px solid var(--border)',
                  borderRadius: '30px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: scanning || cameraLoading ? 'not-allowed' : 'pointer',
                  opacity: scanning || cameraLoading ? 0.6 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FaCamera /> {cameraLoading ? 'Ouverture...' : 'Scanner webcam'}
              </button>
            </div>
            <p style={{ color: 'var(--gray)', fontSize: '0.85rem', marginTop: '1.5rem' }}>
              Moteur : {providerMeta.label} · {providerMeta.hint}
            </p>
          </div>
        ) : displayedPhotos.length === 0 && showOnlyMatches ? (
          <div style={{
            padding: '4rem', textAlign: 'center',
            background: 'var(--surface)', borderRadius: '15px',
            boxShadow: '0 4px 8px var(--shadow)'
          }}>
            <FaUser style={{ fontSize: '4rem', color: scanAccent, marginBottom: '1rem' }} />
            <h3 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>Aucune correspondance trouvée</h3>
            <p style={{ color: 'var(--gray)' }}>Your face was not detected in any photo from this event.</p>
          </div>
        ) : photos.length === 0 ? (
          <div style={{
            padding: '4rem', textAlign: 'center',
            background: 'var(--surface)', borderRadius: '15px',
            boxShadow: '0 4px 8px var(--shadow)'
          }}>
            <FaImages style={{ fontSize: '4rem', color: 'var(--gray)', marginBottom: '1rem' }} />
            <h3 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>Aucune photo pour le moment</h3>
            <p style={{ color: 'var(--gray)' }}>Importez des photos dans cet événement pour les voir ici</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '1.5rem'
          }}>
            {displayedPhotos.map((photo) => {
              const index = photos.findIndex(p => p.id === photo.id);
              const isSelected = selectedPhotos.includes(photo.id);
              const isInCart = cartPhotoIds.includes(photo.id);
              const canDownload = canDownloadPhoto(photo, isEventOwner);
              const blobSrc = photoBlobs[photo.id];
              const matchData = scanResult?.matched_photos.find(m => m.id === photo.id);
              return (
                <div
                  key={photo.id}
                  style={{
                    position: 'relative', background: '#FFFFFF',
                    borderRadius: '12px', overflow: 'hidden',
                    boxShadow: isSelected
                      ? '0 0 0 4px var(--primary-orange)'
                      : matchData
                        ? `0 0 0 3px ${scanAccent}, 0 4px 12px ${scanAccent}40`
                        : '0 4px 8px var(--shadow)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Selection checkbox */}
                  <button
                    onClick={() => togglePhotoSelection(photo.id)}
                    style={{
                      position: 'absolute', top: '8px', left: '8px',
                      background: 'rgba(0,0,0,0.45)', border: 'none',
                      color: isSelected ? 'var(--primary-orange)' : 'white',
                      fontSize: '1.3rem', cursor: 'pointer', zIndex: 10,
                      borderRadius: '6px', padding: '4px',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    {isSelected ? <FaCheckSquare /> : <FaSquare />}
                  </button>

                  {/* Expand / view full size button */}
                  <button
                    onClick={() => openLightbox(index)}
                    style={{
                      position: 'absolute', top: '8px', right: '8px',
                      background: 'rgba(0,0,0,0.45)', border: 'none',
                      color: 'white', fontSize: '1rem', cursor: 'pointer',
                      zIndex: 10, borderRadius: '6px', padding: '6px 8px',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <FaExpand />
                  </button>

                  {/* Visibility badge */}
                  {photo.visibility === 'private' && (
                    <div style={{
                      position: 'absolute', top: '48px', right: '8px',
                      background: 'rgba(0,0,0,0.6)', color: 'white',
                      fontSize: '0.75rem', padding: '2px 7px', borderRadius: '10px',
                      zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px',
                    }}>
                      <FaEyeSlash /> Privé
                    </div>
                  )}
                  {photo.visibility === 'public' && (
                    <div style={{
                      position: 'absolute', top: '48px', right: '8px',
                      background: 'rgba(34,197,94,0.6)', color: 'white',
                      fontSize: '0.75rem', padding: '2px 7px', borderRadius: '10px',
                      zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px',
                    }}>
                      <FaEye /> Public
                    </div>
                  )}

                  {matchData && (
                    <div style={{
                      position: 'absolute', bottom: '8px', left: '8px',
                      background: `${scanAccent}e6`, color: scanOnAccent,
                      fontSize: '0.75rem', padding: '3px 8px', borderRadius: '10px',
                      zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px',
                      fontWeight: '700',
                    }}>
                      <FaUser /> {matchData.confidence_score.toFixed(1)}%
                      {' '}{matchData.similarity != null ? 'similarité' : 'match'}
                    </div>
                  )}

                  <img
                    src={blobSrc || 'https://placehold.co/300x200/png'}
                    alt={photo.filename}
                    onClick={() => openLightbox(index)}
                    style={{
                      width: '100%', height: '200px', objectFit: 'cover',
                      cursor: 'zoom-in', display: 'block',
                    }}
                    onError={(e) => { e.target.src = 'https://placehold.co/300x200/png'; }}
                  />

                  <div style={{ padding: '1rem' }}>
                    <div
                      title={getPhotoDisplayName(photo)}
                      style={{
                        color: 'var(--dark-gray)',
                        fontSize: '0.95rem',
                        fontWeight: '700',
                        marginBottom: '0.75rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getPhotoDisplayName(photo)}
                    </div>

                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.9rem', color: 'var(--gray)', marginBottom: '0.5rem'
                    }}>
                      <span>📊 {toNumber(photo.matches_count)} matches</span>
                      <span>💾 {toNumber(photo.file_size_mb).toFixed(2)} MB</span>
                    </div>

                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.9rem', color: 'var(--gray)', marginBottom: '0.75rem'
                    }}>
                      <span>⬇️ {toNumber(photo.downloads_count)} downloads</span>
                      <span>{new Date(photo.uploaded_at).toLocaleDateString()}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', gap: '0.5rem' }}>
                      <div style={{
                        display: 'inline-block', padding: '0.3rem 0.8rem',
                        borderRadius: '20px', fontSize: '0.85rem', fontWeight: '700',
                        background: photo.price > 0 ? 'var(--primary-orange)' : 'var(--primary-green)',
                        color: 'white',
                      }}>
                      {photo.price > 0 ? `${parseFloat(photo.price).toFixed(2)} F` : 'Gratuit'}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleLike(photo); }}
                        title={photo.is_liked ? 'Unlike this photo' : 'Like this photo'}
                        style={{
                          padding: '0.3rem 0.7rem',
                          background: photo.is_liked ? 'var(--primary)' : 'var(--surface-2)',
                          color: photo.is_liked ? 'white' : 'var(--gray)',
                          border: photo.is_liked ? 'none' : '1px solid var(--border)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                        }}
                      >
                        <FaHeart /> {toNumber(photo.likes_count)}
                      </button>
                      {canDownload ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadSingle(photo); }}
                          title="Télécharger cette photo"
                          style={{
                            padding: '0.3rem 0.7rem', background: 'var(--primary-orange)',
                            color: 'white', border: 'none', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.85rem',
                            display: 'flex', alignItems: 'center', gap: '0.3rem',
                          }}
                        >
                          <FaDownload /> {photo.has_purchased && !isEventStaff ? 'Achetée' : ''}
                        </button>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleCartPhoto(photo); }}
                          title={isInCart ? 'Retirer du panier' : 'Ajouter au panier'}
                          style={{
                            padding: '0.3rem 0.7rem',
                            background: isInCart ? 'var(--primary-green)' : 'var(--primary-orange)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                          }}
                        >
                          <FaShoppingCart /> {isInCart ? 'In Cart' : 'Add'}
                        </button>
                      )}
                    </div>

                    {isEventOwner && (
                      <div
                        style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          onClick={() => setEditingPhoto(photo)}
                          style={{
                            flex: 1, padding: '0.5rem',
                            background: 'var(--primary-orange)', color: 'white',
                            border: 'none', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                          }}
                        >
                          <FaEdit /> Modifier
                        </button>
                        <button
                          onClick={() => handleDeletePhoto(photo.id)}
                          disabled={deletingPhoto === photo.id}
                          style={{
                            flex: 1, padding: '0.5rem',
                            background: deletingPhoto === photo.id ? 'var(--gray)' : '#EF4444',
                            color: 'white', border: 'none', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                          }}
                        >
                          <FaTrash /> {deletingPhoto === photo.id ? '...' : 'Supprimer'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      {/* Lightbox / Slideshow */}
      {lightboxIndex !== null && currentPhoto && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)',
            zIndex: 2000, display: 'flex', flexDirection: 'column',
          }}
          onClick={closeLightbox}
        >
          {/* Top bar */}
          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1rem 1.5rem',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
              position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
            }}
            onClick={e => e.stopPropagation()}
          >
            <span style={{ color: 'white', fontWeight: '600', fontSize: '1rem' }}>
              {lightboxIndex + 1} / {photos.length}
              {event?.name && <span style={{ marginLeft: '1rem', opacity: 0.7 }}>{event.name}</span>}
            </span>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {canDownloadPhoto(currentPhoto, isEventOwner) && (
                <button
                  onClick={() => handleDownloadSingle(currentPhoto)}
                  title="Télécharger cette photo"
                  style={{
                    background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                    padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '1.1rem',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  <FaDownload /> Télécharger
                </button>
              )}
              <button
                onClick={() => setSlideshowActive(s => !s)}
                title={slideshowActive ? 'Pause slideshow' : 'Play slideshow'}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                  padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '1.1rem',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                {slideshowActive ? <FaPause /> : <FaPlay />}
                {slideshowActive ? 'Pause' : 'Play'}
              </button>
              <button
                onClick={closeLightbox}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                  padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '1.2rem',
                }}
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Prev button */}
          <button
            onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
            style={{
              position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
              fontSize: '1.8rem', padding: '14px 18px', borderRadius: '50%',
              cursor: 'pointer', zIndex: 10,
            }}
          >
            <FaArrowLeft />
          </button>

          {/* Image */}
          <div
            style={{
              flex: 1,
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '88px clamp(12px, 7vw, 80px) 118px',
              boxSizing: 'border-box',
            }}
            onClick={e => e.stopPropagation()}
          >
            <img
              src={photoBlobs[currentPhoto.id] || 'https://placehold.co/800x600/png'}
              alt={currentPhoto.filename}
              style={{
                display: 'block',
                width: 'auto',
                height: 'auto',
                maxWidth: 'calc(100vw - clamp(24px, 14vw, 160px))',
                maxHeight: 'calc(100vh - 206px)',
                objectFit: 'contain',
                borderRadius: '4px',
                boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              }}
            />
          </div>

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
            style={{
              position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
              fontSize: '1.8rem', padding: '14px 18px', borderRadius: '50%',
              cursor: 'pointer', zIndex: 10,
            }}
          >
            <FaArrowRight />
          </button>

          {/* Bottom info */}
          <div
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: '1rem 1.5rem',
              background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
              color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              minHeight: '86px',
              boxSizing: 'border-box',
              gap: '1rem',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>
              {currentPhoto.price > 0
                ? `${parseFloat(currentPhoto.price).toFixed(2)} F`
                : 'Gratuit'}
              {' · '}
              {currentPhoto.file_size_mb?.toFixed(2)} MB
            </div>

            {/* Slideshow progress bar */}
            {slideshowActive && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                height: '3px', background: 'rgba(255,255,255,0.2)',
              }}>
                <div
                  key={lightboxIndex}
                  style={{
                    height: '100%', background: 'var(--primary-orange)',
                    animation: 'lbProgress 4s linear forwards',
                  }}
                />
              </div>
            )}

            {/* Thumbnails strip */}
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', maxWidth: '60vw', padding: '2px' }}>
              {photos.map((p, i) => (
                <img
                  key={p.id}
                  src={photoBlobs[p.id] || 'https://placehold.co/60x40/png'}
                  alt=""
                  onClick={() => setLightboxIndex(i)}
                  style={{
                    width: '52px', height: '36px', objectFit: 'cover',
                    borderRadius: '4px', cursor: 'pointer', flexShrink: 0,
                    outline: i === lightboxIndex ? '2px solid var(--primary-orange)' : '2px solid transparent',
                    opacity: i === lightboxIndex ? 1 : 0.55,
                    transition: 'all 0.2s',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes lbProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>

      <PhotoEditModal
        photo={editingPhoto}
        blobUrl={editingPhoto ? photoBlobs[editingPhoto.id] : null}
        onClose={() => setEditingPhoto(null)}
        onSaved={handlePhotoSaved}
      />

    </>
  );
};

export default EventBrowse;
